import { useCallback, useMemo, useState } from 'react'

import { useConnect } from 'redux-bundler-hook'

import { PieChartRounded } from '@mui/icons-material'
import { Box, Button, Chip, Stack } from '@mui/material'
import { DateRangePicker } from '@mui/x-date-pickers-pro'

import { humanize } from 'inflection'
import { DateTime } from 'luxon'

import { SearchBox } from '@common/components'
import { useSmallScreen } from '@common/utils'
import { buildReasonsById } from '@common/utils/adjustmentReasonsUtils'
import { AdjustmentSummaryPopover } from '@rest/UI/components'
import { EventList } from '@rest/UI/components/EventList'
import ExportEventsButton from '@rest/UI/components/EventList/components/ExportEventsButton'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.property
 * @param {string} props.property.id
 * @param {string} props.property.name
 * @param {boolean} props.property.reservationGrouping
 * @param {string[]} [props.property.expandedFlags]
 * @param {string} props.property.propertyType
 * @param {string} props.property.timezone
 * @param {number} props.property.unitCount
 * @param {Object} props.unit
 * @param {string} props.unit.id
 * @param {string} props.unit.name
 */
export default function EventsTab({ property, unit }) {
  const {
    eventListRaw: { filter = {} },
    eventListIsLoading,
    smokeAdjustmentReasons,
    doEventListSetSearch,
    doEventListSetFilter,
  } = useConnect(
    'selectEventListRaw',
    'selectEventListIsLoading',
    'selectSmokeAdjustmentReasons',
    'doEventListSetSearch',
    'doEventListSetFilter',
  )

  const isSmallScreen = useSmallScreen()

  const defaultEnd = DateTime.now()
  const defaultStart = DateTime.now().minus({ days: 30 })

  const [dateRangeValue, setDateRangeValue] = useState([defaultStart, defaultEnd])
  const [adjustmentSummaryAnchorEl, setAdjustmentSummaryAnchorEl] = useState(null)

  const reasonsById = useMemo(
    () => buildReasonsById(smokeAdjustmentReasons),
    [smokeAdjustmentReasons],
  )

  const FilterChip = useCallback(() => {
    const { reason } = filter
    if (!reason) return null

    return (
      <Chip
        component="span"
        label={reasonsById[reason] || humanize(reason)}
        size="small"
        onDelete={() => {
          doEventListSetFilter({ ...filter, id: null, reason: null })
        }}
      />
    )
  }, [filter])

  return (
    <>
      <AdjustmentSummaryPopover
        anchorEl={adjustmentSummaryAnchorEl}
        dateRange={dateRangeValue}
        reasonsById={reasonsById}
        unitId={unit.id}
        onClose={() => setAdjustmentSummaryAnchorEl(false)}
        onAdjustmentClick={(data) => {
          doEventListSetFilter({
            ...filter,
            id: data.eventsIds.join(','),
            reason: data.reason,
          })
        }}
      />

      <Stack
        direction={isSmallScreen.mobile ? 'column' : 'row'}
        justifyContent="space-between"
        mt={1}
        mb={1}
      >
        {!isSmallScreen.mobile && (
          <Box display="flex" alignItems="center">
            <SearchBox title="Units" onSetSearch={doEventListSetSearch} minLength={2} />
            <FilterChip />
          </Box>
        )}
        <Stack
          direction={isSmallScreen.mobile ? 'column' : 'row'}
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          <DateRangePicker
            disableMaskedInput
            value={dateRangeValue}
            maxDate={defaultEnd}
            onChange={setDateRangeValue}
            disabled={eventListIsLoading}
            slotProps={{
              fieldSeparator: { children: 'and' },
              textField: {
                size: 'small',
                style: { width: !isSmallScreen.mobile ? 250 : '100%' },
                required: true,
              },
            }}
          />
          {isSmallScreen.mobile && <FilterChip />}
          <Box
            display="flex"
            width={isSmallScreen.mobile ? '100%' : null}
            sx={{ pb: isSmallScreen.mobile ? 1 : 0 }}
          >
            <Button
              fullWidth={isSmallScreen.mobile}
              data-testid="adjustment_summary"
              variant="contained"
              disabled={eventListIsLoading}
              startIcon={!isSmallScreen.mobile ? <PieChartRounded /> : null}
              onClick={(event) => setAdjustmentSummaryAnchorEl(event.currentTarget)}
              sx={{ ml: isSmallScreen.mobile ? 0 : 2 }}
            >
              Adjustment Summary
            </Button>
            <ExportEventsButton entity={unit} />
          </Box>
        </Stack>
      </Stack>
      <EventList property={property} unit={unit} dateRange={dateRangeValue} />
    </>
  )
}
